import config from "../../config";
import store from "../store";

export default {
  namespaced: true,
  state: {
    turnos: [],
    lavadores: [],
    areas: [],
    loading: false,
    error: null,
    permisos: null, // null = not fetched yet; object = { lectura, gestion, esAdmin, esLavador }
    permisosLoading: false,
  },
  mutations: {
    SET_TURNOS(state, payload) {
      state.turnos = payload;
    },
    SET_LAVADORES(state, payload) {
      state.lavadores = payload;
    },
    SET_AREAS(state, payload) {
      state.areas = payload;
    },
    SET_LOADING(state, payload) {
      state.loading = payload;
    },
    SET_ERROR(state, payload) {
      state.error = payload;
    },
    SET_PERMISOS(state, payload) {
      state.permisos = payload;
    },
    SET_PERMISOS_LOADING(state, payload) {
      state.permisosLoading = payload;
    },
    UPDATE_TURNO_ESTADO(state, { id, nuevo_estado }) {
      const index = state.turnos.findIndex((t) => t.id === id);
      if (index !== -1) {
        state.turnos[index].estado_lavadero = nuevo_estado;
        state.turnos = [...state.turnos];
      }
    },
    UPDATE_TURNO_LAVADOR(state, { id, lavador_id, lavador_nombre }) {
      const index = state.turnos.findIndex((t) => t.id === id);
      if (index !== -1) {
        state.turnos[index].lavador_id = lavador_id;
        state.turnos[index].lavador_nombre = lavador_nombre;
        state.turnos = [...state.turnos];
      }
    },
    UPDATE_TURNO_EQUIPO(state, { id, equipo }) {
      const index = state.turnos.findIndex((t) => t.id === id);
      if (index !== -1) {
        state.turnos[index].equipo = equipo;
        state.turnos = [...state.turnos];
      }
    },
    UPDATE_TURNO_COMENTARIO(state, { id, comentario }) {
      const index = state.turnos.findIndex((t) => t.id === id);
      if (index !== -1) {
        state.turnos[index].comentario = comentario;
        state.turnos = [...state.turnos];
      }
    },
    UPDATE_TURNO_UNIDAD(state, { id, unidad }) {
      const index = state.turnos.findIndex((t) => t.id === id);
      if (index !== -1) {
        state.turnos[index].patente = unidad;
        state.turnos = [...state.turnos];
      }
    },
  },
  actions: {
    /**
     * Obtiene los permisos reales del usuario actual desde el backend.
     * La respuesta incluye { permisos: { lectura, gestion, esAdmin, esLavador }, codigos }.
     * Se guarda en state.permisos. Null = aún no cargado.
     */
    async fetchPermisos({ commit, state }) {
      if (state.permisosLoading) return;
      commit("SET_PERMISOS_LOADING", true);
      try {
        const res = await fetch(`${config.API_URL}/lavadero/permisos`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: store.state.token,
          },
        });
        const data = await res.json();
        if (data.exito === 1 && data.permisos) {
          commit("SET_PERMISOS", data.permisos);
        } else {
          // Fallback conservador: no conceder nada si el endpoint falla
          commit("SET_PERMISOS", { lectura: false, gestion: false, esAdmin: false, esLavador: false });
        }
      } catch (error) {
        console.error("[lavadero] Error al obtener permisos:", error.message);
        commit("SET_PERMISOS", { lectura: false, gestion: false, esAdmin: false, esLavador: false });
      } finally {
        commit("SET_PERMISOS_LOADING", false);
      }
    },

    async fetchTurnos({ commit }, filtros = {}) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const queryParams = new URLSearchParams();
        if (filtros.fecha_desde) queryParams.set("fecha_desde", filtros.fecha_desde);
        if (filtros.fecha_hasta) queryParams.set("fecha_hasta", filtros.fecha_hasta);
        if (filtros.sucursal) queryParams.set("sucursal", filtros.sucursal);
        if (filtros.estado && filtros.estado !== "todos") {
          queryParams.set("estado", filtros.estado);
        }

        const queryString = queryParams.toString();
        const url = `${config.API_URL}/lavadero/turnos${queryString ? "?" + queryString : ""}`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: store.state.token,
          },
        });

        const data = await res.json();

        if (data.exito === 1) {
          commit("SET_TURNOS", data.data || []);
        } else {
          commit("SET_TURNOS", []);
          store.dispatch("show_snackbar", {
            text: data.message || "Error al cargar turnos",
            color: "error",
          });
        }
      } catch (error) {
        commit("SET_TURNOS", []);
        commit("SET_ERROR", error.message);
        store.dispatch("show_snackbar", {
          text: "Error de conexión al cargar turnos: " + error.message,
          color: "error",
        });
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchLavadores({ commit }) {
      try {
        const res = await fetch(`${config.API_URL}/lavadero/lavadores`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: store.state.token,
          },
        });

        const data = await res.json();

        if (data.exito === 1) {
          commit("SET_LAVADORES", data.data || []);
        }
      } catch (error) {
        console.error("Error al cargar lavadores:", error.message);
      }
    },

    async fetchAreas({ commit }) {
      try {
        const res = await fetch(`${config.API_URL}/areas`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: store.state.token,
          },
        });

        const data = await res.json();

        if (data.exito === 1) {
          commit("SET_AREAS", data.data || []);
        }
      } catch (error) {
        console.error("Error al cargar áreas:", error.message);
      }
    },

    async cambiarEstado({ commit, dispatch }, { id, fuente, nuevo_estado, lavador_id, lavador_nombre, incidentes, equipo }) {
      try {
        const res = await fetch(
          `${config.API_URL}/lavadero/turnos/${id}/estado`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: store.state.token,
            },
            body: JSON.stringify({ fuente, nuevo_estado, lavador_id, lavador_nombre, incidentes, equipo }),
          }
        );

        const data = await res.json();

        if (data.exito === 1) {
          commit("UPDATE_TURNO_ESTADO", { id, nuevo_estado });
          // Al volver a pendiente, limpiar visibilidad del lavador asignado
          if (nuevo_estado === "pendiente") {
            commit("UPDATE_TURNO_LAVADOR", {
              id,
              lavador_id: null,
              lavador_nombre: null,
            });
            commit("UPDATE_TURNO_EQUIPO", { id, equipo: 0 });
          }
          store.dispatch("show_snackbar", {
            text: data.message || "Estado actualizado",
            color: "success",
          });
          return { exito: 1, message: data.message };
        } else {
          store.dispatch("show_snackbar", {
            text: data.message || "Error al cambiar estado",
            color: "error",
          });
          await dispatch("fetchTurnos");
          return { exito: 0, message: data.message || "Error al cambiar estado" };
        }
      } catch (error) {
        store.dispatch("show_snackbar", {
          text: "Error de conexión al cambiar estado: " + error.message,
          color: "error",
        });
        return { exito: 0, message: error.message };
      }
    },

    async asignarLavador({ commit, dispatch }, { id, lavador_id, lavador_nombre, equipo }) {
      try {
        const res = await fetch(
          `${config.API_URL}/lavadero/turnos/${id}/asignar`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: store.state.token,
            },
            body: JSON.stringify({ lavador_id, lavador_nombre, equipo }),
          }
        );

        const data = await res.json();

        if (data.exito === 1) {
          commit("UPDATE_TURNO_LAVADOR", { id, lavador_id, lavador_nombre });
          commit("UPDATE_TURNO_EQUIPO", { id, equipo: equipo || 0 });
          store.dispatch("show_snackbar", {
            text: data.message || "Lavador asignado correctamente",
            color: "success",
          });
          return { exito: 1 };
        } else {
          store.dispatch("show_snackbar", {
            text: data.message || "Error al asignar lavador",
            color: "error",
          });
          return { exito: 0, message: data.message };
        }
      } catch (error) {
        store.dispatch("show_snackbar", {
          text: "Error de conexión al asignar lavador: " + error.message,
          color: "error",
        });
        return { exito: 0, message: error.message };
      }
    },

    async crearTurno({ dispatch }, payload) {
      const res = await fetch(`${config.API_URL}/lavadero/turnos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: store.state.token,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.exito !== 1) {
        throw new Error(data.message || "Error al crear el turno");
      }

      return data;
    },

    async buscarVehiculo({}, patente) {
      const res = await fetch(
        `${config.API_URL}/lavadero/vehiculos/buscar?patente=${encodeURIComponent(patente)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: store.state.token,
          },
        }
      );

      const data = await res.json();
      return data;
    },

    /**
     * Obtiene métricas históricas del lavadero desde LavaderoAuditLog.
     * @param {object} filtros — { fecha_desde, fecha_hasta, sucursal }
     * @returns {{ exito: 1|0, data: array }}
     */
    async fetchMetricasHistoricas({ commit }, filtros = {}) {
      try {
        const queryParams = new URLSearchParams();
        if (filtros.fecha_desde) queryParams.set("fecha_desde", filtros.fecha_desde);
        if (filtros.fecha_hasta) queryParams.set("fecha_hasta", filtros.fecha_hasta);
        if (filtros.sucursal) queryParams.set("sucursal", filtros.sucursal);

        const queryString = queryParams.toString();
        const url = `${config.API_URL}/lavadero/metricas-historicas${queryString ? "?" + queryString : ""}`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: store.state.token,
          },
        });

        const data = await res.json();
        return data;
      } catch (error) {
        console.error("[lavadero] Error al obtener métricas históricas:", error.message);
        store.dispatch("show_snackbar", {
          text: "Error de conexión al cargar métricas: " + error.message,
          color: "error",
        });
        return { exito: 0, message: error.message };
      }
    },

    /**
     * POST /lavadero/metricas-diarias/snapshot
     * Guarda/actualiza un snapshot de turnos pendientes para un día objetivo.
     * @param {object} payload — { fecha_dia (YYYY-MM-DD), sucursal (number, opcional) }
     * @returns {{ exito: 1|0, message, snapshot_id, pendientes_guardados, pendientes_nuevos }}
     */
    async guardarSnapshotDiario({ commit }, payload = {}) {
      try {
        const res = await fetch(`${config.API_URL}/lavadero/metricas-diarias/snapshot`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: store.state.token,
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (data.exito === 1) {
          if (!payload.silent) {
            store.dispatch("show_snackbar", {
              text: data.message,
              color: data.pendientes_nuevos > 0 ? "success" : "info",
            });
          }
        } else {
          store.dispatch("show_snackbar", {
            text: data.message || "Error al guardar snapshot",
            color: "error",
          });
        }
        return data;
      } catch (error) {
        console.error("[lavadero] Error al guardar snapshot diario:", error.message);
        store.dispatch("show_snackbar", {
          text: "Error de conexión al guardar snapshot: " + error.message,
          color: "error",
        });
        return { exito: 0, message: error.message };
      }
    },

    async actualizarComentario({ commit, dispatch }, { id, comentario }) {
      try {
        const res = await fetch(
          `${config.API_URL}/lavadero/turnos/${encodeURIComponent(id)}/comentario`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: store.state.token,
            },
            body: JSON.stringify({ comentario }),
          }
        );

        const data = await res.json();

        if (data.exito === 1) {
          commit("UPDATE_TURNO_COMENTARIO", { id, comentario });
          store.dispatch("show_snackbar", {
            text: data.message || "Horario de retiro actualizado",
            color: "success",
          });
          return { exito: 1 };
        } else {
          store.dispatch("show_snackbar", {
            text: data.message || "Error al actualizar el horario de retiro",
            color: "error",
          });
          return { exito: 0, message: data.message };
        }
      } catch (error) {
        store.dispatch("show_snackbar", {
          text: "Error de conexión al actualizar: " + error.message,
          color: "error",
        });
        return { exito: 0, message: error.message };
      }
    },

    async modificarUnidad({ commit }, { id, unidad }) {
      try {
        const res = await fetch(
          `${config.API_URL}/lavadero/turnos/${encodeURIComponent(id)}/unidad`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: store.state.token,
            },
            body: JSON.stringify({ unidad }),
          }
        );

        const data = await res.json();
        const unidadActualizada =
          data.data?.unidad || String(unidad || "").trim().toUpperCase();

        if (data.exito === 1) {
          commit("UPDATE_TURNO_UNIDAD", { id, unidad: unidadActualizada });
          store.dispatch("show_snackbar", {
            text: data.message || "Unidad actualizada correctamente",
            color: "success",
          });
          return { exito: 1, unidad: unidadActualizada, data: data.data };
        }

        store.dispatch("show_snackbar", {
          text: data.message || "Error al actualizar la unidad",
          color: "error",
        });
        return { exito: 0, message: data.message || "Error al actualizar la unidad" };
      } catch (error) {
        store.dispatch("show_snackbar", {
          text: "Error de conexión al actualizar la unidad: " + error.message,
          color: "error",
        });
        return { exito: 0, message: error.message };
      }
    },
  },
  getters: {
    /**
     * Retorna si el usuario actual es administrador de lavadero.
     */
    esAdminLavadero(state, getters, rootState) {
      if (state.permisos) return !!state.permisos.esAdmin;

      const usuario = rootState.usuario;
      if (!usuario || Object.keys(usuario).length === 0) return false;
      const permisos = usuario.permisos;
      if (!permisos) return false;
      if (Array.isArray(permisos)) return permisos.includes("taller.lavadero.administrador");
      if (typeof permisos === "string") {
        return permisos.split(",").map((p) => p.trim()).includes("taller.lavadero.administrador");
      }
      if (typeof permisos === "object") {
        return !!permisos["taller.lavadero.administrador"];
      }
      return false;
    },
  },
};
